import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InMemoryStorageService } from '../../services/in-memory-storage.service';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { UserRole } from '../../auth/roles.enum';
import { PRE_POST_TEXT_TYPES, PrePostWizardResult, PrepostTextWizardComponent } from '../../shared/prepost-text-wizard/prepost-text-wizard.component';
import { ItemDetailsService } from '../../services/item-details.service';
import { resolveCreditLimit3 } from '../item-utils';
import {
    applySelectedCustomerAddress,
    CustomerAddressOption,
    mapCustomerAddressesFromDetails,
    resolveCustomOrListAddress
} from '../customer-address.util';


@Component({
  selector: 'app-header-info',
  templateUrl: './header-info.component.html',
  styleUrls: ['./header-info.component.scss'],
  imports: [CommonModule, AutoCompleteModule, FormsModule, ButtonModule, DialogModule, PrepostTextWizardComponent],
  providers: [MessageService]   // ensure MessageService is available
})
export class HeaderInfoComponent implements OnChanges, OnDestroy {
  @Input() item: any;
  @Input() product: any;
  @Input() readOnly: boolean = false;
  @Input() company!: string;
  @Input() division!: string;
  @Input() userRoles: UserRole[] = [];     // Receive from parent
  @Input()
  isReadOnly!: boolean;

  /** V2 sidebar: stack Customer above Header; default keeps V1 side-by-side layout */
  @Input() layoutVariant: 'default' | 'v2' = 'default';

  /** When true, each section title toggles visibility of that panel (V2) */
  @Input() sectionCollapseEnabled = false;

  /** Loaded by parent after SearchCustomer/Details (same as Orders / Orders1). */
  @Input() orderTypeOptions: { label: string; value: string }[] = [];

  @Output() addressChanged = new EventEmitter<void>();

  customerSectionExpanded = true;
  headerSectionExpanded = true;

  readonly uca2MaxLength = 20;

  orderType: any[] = [];
  filteredOrderType: any[] = [];
    isHeaderEditable= false;
  backupHeader: any = {}; // to store original values when editing starts

  showAddressDialog = false;
  addressDraft: string | CustomerAddressOption | null = '';
  filteredAddresses: CustomerAddressOption[] = [];
  addressOptions: CustomerAddressOption[] = [];
  loadingAddresses = false;

  private destroy$ = new Subject<void>();

  constructor(
    private inMemoryStorage: InMemoryStorageService,
    private messageService: MessageService,
    private itemDetailsService: ItemDetailsService
  ) {}

  showTextWizardDialog = false;
  selectedTextType: string = '';
  readonly PRE_POST_TEXT_TYPES = PRE_POST_TEXT_TYPES;

  /** User saved Pre-Text via the wizard (do not overwrite with Extra Info). */
  hasManualHeaderPreText = false;
  /** Pre-Text is driven by Extra information; Pre-Text button stays disabled. */
  preTextLockedByExtraInfo = false;
  private extraInfoPreTextSaveTimer: ReturnType<typeof setTimeout> | null = null;

  /** Same resolved limit as credit-limit banner and dialogs. */
  get creditLimitDisplay(): string {
    const n = resolveCreditLimit3({
      okcrl3: this.item?.okcrl3,
      creditLimit3: this.item?.creditLimit3
    });
    return Number.isFinite(n) ? n.toFixed(2) : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderTypeOptions']) {
      this.orderType = this.orderTypeOptions ?? [];
      this.filteredOrderType = this.orderType;
    }
    if (changes['item']) {
      this.syncAddressOptionsFromItem();
    }
  }

  private syncAddressOptionsFromItem(): void {
    const opts = (this.item?.customerAddressOptions || this.product?.customerAddressOptions || []) as CustomerAddressOption[];
    this.addressOptions = Array.isArray(opts) ? [...opts] : [];
    this.filteredAddresses = [...this.addressOptions];
  }

  filterOrderTypes(event: any) {
    const query = event.query.toLowerCase();
    this.filteredOrderType = this.orderType.filter(o =>
      o.label.toLowerCase().includes(query)
    );
  }

  filterAddresses(event: any): void {
    const query = (event?.query || '').toLowerCase();
    if (!query) {
      this.filteredAddresses = [...this.addressOptions];
      return;
    }
    this.filteredAddresses = this.addressOptions.filter((a) => a.label.toLowerCase().includes(query));
  }

  ngOnDestroy() {
    this.clearExtraInfoPreTextSaveTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }



enableHeaderEdit() {
  this.isHeaderEditable = true;
  console.log('Edit button clicked!');
}


toggleHeaderEdit() {
  if (this.readOnly) {
    console.warn('Editing not allowed due to order status.');
    return;
  }

  if (!this.isHeaderEditable) {
    // Enter edit mode → keep a copy
    this.backupHeader = { ...this.item };
    this.isHeaderEditable = true;
    if (this.sectionCollapseEnabled) {
      // On Edit click, ensure header section opens.
      this.headerSectionExpanded = true;
    }
  } else {
    // Save mode
    console.log('Saving header data:', this.item);
    this.isHeaderEditable = false;
  }
}

collapseAllSections(): void {
  if (!this.sectionCollapseEnabled) {
    return;
  }
  this.headerSectionExpanded = false;
  this.customerSectionExpanded = false;
}

expandAllSections(): void {
  if (!this.sectionCollapseEnabled) {
    return;
  }
  this.headerSectionExpanded = true;
  this.customerSectionExpanded = true;
}

cancelHeaderEdit() {
  this.item = { ...this.backupHeader };  // restore backup
  this.isHeaderEditable = false;
}

openAddressDialog(): void {
  this.addressDraft = this.item?.address || '';
  this.syncAddressOptionsFromItem();
  this.showAddressDialog = true;
  if (this.addressOptions.length === 0) {
    this.loadAddressesIfNeeded();
  }
}

closeAddressDialog(): void {
  this.showAddressDialog = false;
}

private loadAddressesIfNeeded(): void {
  const customerId = this.item?.customerId || this.item?.customer || '';
  const company = this.company || this.inMemoryStorage.get('company') || '';
  const division = this.division || this.inMemoryStorage.get('division') || '';
  const faci = this.item?.facilityId || this.item?.facility || '100';
  if (!customerId || !company || !division || this.loadingAddresses) {
    return;
  }
  this.loadingAddresses = true;
  this.itemDetailsService
    .getPriceListInfo(String(customerId), String(company), String(division), String(faci))
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (resp) => {
        this.loadingAddresses = false;
        const options = mapCustomerAddressesFromDetails(resp);
        this.addressOptions = options;
        this.filteredAddresses = [...options];
        if (this.item) {
          this.item.customerAddressOptions = options;
        }
        if (this.product) {
          this.product.customerAddressOptions = options;
        }
      },
      error: () => {
        this.loadingAddresses = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Address load failed',
          detail: 'Could not load customer addresses. Please try again.',
          life: 4000
        });
      }
    });
}

onAddressDraftInput(value: string | CustomerAddressOption | null): void {
  if (value != null && typeof value === 'object' && 'label' in value) {
    return;
  }
  const text = typeof value === 'string' ? value.trim() : '';
  const idMatch = this.item?.addressId
    ? this.addressOptions.find((a) => a.addressId === this.item.addressId)
    : undefined;
  if (idMatch && idMatch.label !== text) {
    this.item.addressId = '';
    this.item.customerAddress = '';
  }
}

applyAddressChange(): void {
  const result = resolveCustomOrListAddress(this.addressDraft, this.addressOptions, this.item?.addressId);
  if (!result.ok) {
    this.messageService.add({
      severity: 'error',
      summary: 'Invalid address',
      detail: result.message,
      life: 4500
    });
    return;
  }
  applySelectedCustomerAddress(this.item, this.product, result.resolved);
  this.showAddressDialog = false;
  this.addressChanged.emit();
  this.messageService.add({
    severity: 'success',
    summary: 'Address updated',
    detail: 'Delivery address has been updated.',
    life: 2500
  });
}

openTextWizardDialog(textType: string) {
  if (textType === PRE_POST_TEXT_TYPES.PRE && this.preTextLockedByExtraInfo) {
    return;
  }
  console.log('Opening text wizard dialog for text type:', textType);
  this.selectedTextType = textType === PRE_POST_TEXT_TYPES.PRE ? PRE_POST_TEXT_TYPES.PRE : PRE_POST_TEXT_TYPES.POST;
  this.showTextWizardDialog = true;
}

/**
 * As soon as Extra Info (uca2) has text and Pre-Text is empty, copy into HEADER_PRE_TEXT
 * for picking list only (CO04) and disable the Pre-Text button. Clearing Extra Info unlocks Pre-Text.
 */
onExtraInfoChange(): void {
  if (this.division !== '100' || this.readOnly || this.isReadOnly) {
    return;
  }
  const text = (this.item?.uca2 || '').trim();
  if (!text) {
    this.clearExtraInfoDrivenPreText();
    return;
  }
  if (this.hasManualHeaderPreText || this.itemDetailsService.hasPendingManualHeaderPreText()) {
    return;
  }
  this.applyExtraInfoAsPreText(text);
}

/** Flush any debounced existing-order Pre-Text save when leaving the field. */
onExtraInfoBlur(): void {
  if (this.division !== '100' || this.readOnly || this.isReadOnly) {
    return;
  }
  if (this.hasManualHeaderPreText || this.itemDetailsService.hasPendingManualHeaderPreText()) {
    return;
  }
  const text = (this.item?.uca2 || '').trim();
  if (!text) {
    this.clearExtraInfoDrivenPreText();
    return;
  }
  this.applyExtraInfoAsPreText(text, true);
}

private clearExtraInfoPreTextSaveTimer(): void {
  if (this.extraInfoPreTextSaveTimer != null) {
    clearTimeout(this.extraInfoPreTextSaveTimer);
    this.extraInfoPreTextSaveTimer = null;
  }
}

/**
 * @param immediate When true (blur), save existing-order Pre-Text without debounce.
 */
private applyExtraInfoAsPreText(text: string, immediate = false): void {
  const orderNumber = this.item?.orderNumber;
  const company = this.company || this.inMemoryStorage.get('company') || '';
  const division = this.division || this.inMemoryStorage.get('division') || '';
  const targets = [{ key: 'CO04', label: 'On the picking list', text }];

  // Lock Pre-Text immediately so the button disables as soon as Extra Info has text.
  this.preTextLockedByExtraInfo = true;

  if (!orderNumber || orderNumber === 'New Order') {
    this.itemDetailsService.upsertPendingHeaderPreTextFromExtraInfo(text);
    return;
  }
  if (!company || !division) {
    return;
  }

  const save = () => {
    this.extraInfoPreTextSaveTimer = null;
    this.itemDetailsService
      .updateOrderHeaderTextByDocuments(orderNumber, company, division, PRE_POST_TEXT_TYPES.PRE, targets)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Pre-Text sync failed',
            detail: err?.error?.message || 'Could not copy Extra information to Pre-Text.',
            life: 4000
          });
        }
      });
  };

  this.clearExtraInfoPreTextSaveTimer();
  if (immediate) {
    save();
  } else {
    this.extraInfoPreTextSaveTimer = setTimeout(save, 400);
  }
}

private clearExtraInfoDrivenPreText(): void {
  this.clearExtraInfoPreTextSaveTimer();
  if (!this.preTextLockedByExtraInfo && !this.itemDetailsService.hasPendingExtraInfoHeaderPreText()) {
    return;
  }
  this.itemDetailsService.clearPendingHeaderPreTextFromExtraInfo();
  this.preTextLockedByExtraInfo = false;

  const orderNumber = this.item?.orderNumber;
  const company = this.company || this.inMemoryStorage.get('company') || '';
  const division = this.division || this.inMemoryStorage.get('division') || '';
  if (!orderNumber || orderNumber === 'New Order' || !company || !division) {
    return;
  }
  const emptyTargets = [{ key: 'CO04', label: 'On the picking list', text: '' }];
  this.itemDetailsService
    .updateOrderHeaderTextByDocuments(orderNumber, company, division, PRE_POST_TEXT_TYPES.PRE, emptyTargets)
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      error: () => {
        /* Unlock already applied; best-effort clear on server. */
      }
    });
}

onPrePostComplete(result: PrePostWizardResult) {
  const orderNumber = this.item?.orderNumber;
  const company = this.company || this.inMemoryStorage.get('company') || '';
  const division = this.division || this.inMemoryStorage.get('division') || '';
  const isPre = (result.textType || '').toUpperCase().includes('PRE');

  if (!company || !division) {
    this.messageService.add({
      severity: 'error',
      summary: 'Save failed',
      detail: 'Missing company/division for pre/post text.',
      life: 4000
    });
    return;
  }

  if (!orderNumber || orderNumber === 'New Order') {
    if (isPre) {
      this.itemDetailsService.clearPendingHeaderPreTextFromExtraInfo();
      this.preTextLockedByExtraInfo = false;
      this.hasManualHeaderPreText = true;
    }
    this.itemDetailsService.queuePendingHeaderText(result.textType, result.targets);
    const typeLabel = isPre ? 'Pre-Text' : 'Post-Text';
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: `${typeLabel} added to Header.`,
      life: 3500
    });
    return;
  }

  this.itemDetailsService.updateOrderHeaderTextByDocuments(orderNumber, company, division, result.textType, result.targets).pipe(takeUntil(this.destroy$)).subscribe({
    next: (message: any) => {
      if (isPre) {
        this.hasManualHeaderPreText = true;
        this.preTextLockedByExtraInfo = false;
      }
      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: typeof message === 'string' ? message : 'Header text updated successfully.',
        life: 3000
      });
    },
    error: (err) => {
      this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: err?.error?.message || 'Could not save header text.',
        life: 4000
      });
    }
  });
}

getOrderTypeLabel(value: any): string {
  const option = this.orderType?.find(opt => opt.value === value);
  return option ? option.label : value;
}

get showHeaderEditButton(): boolean {
  if (!this.item) return false;

  const movingStatus = (this.item.orderMovingStatus || this.item.movingStatus || '').toUpperCase();
  return !(this.item.status === 20 || movingStatus === 'INV' || movingStatus === 'WIP');
}

onDateChange(event: any) {
  if (!event.target.value) {
    const today = new Date().toISOString().split('T')[0]; // yyyy-MM-dd
    this.item.requestedDate = today;
  }
}

onHeaderBarClick(event: MouseEvent): void {
  if (!this.sectionCollapseEnabled) {
    return;
  }
  const target = event.target as HTMLElement;
  if (target.closest('button')) {
    return;
  }
  this.headerSectionExpanded = !this.headerSectionExpanded;
}

toggleCustomerSection(): void {
  if (!this.sectionCollapseEnabled) {
    return;
  }
  this.customerSectionExpanded = !this.customerSectionExpanded;
}

}
